import { ArrowLeft, ArrowRight, ExternalLink } from 'lucide-react';

import { cn } from '@/lib/shadcn/utils';
import type { CandidateJobApi } from '@/modules/candidates/types';
import {
  candidateCurrentWork,
  candidateFullName,
} from '@/modules/hiring-manager/utils/candidateDisplay';
import { Button } from '@/modules/ui/components/button/Button';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';
import { Textarea } from '@/modules/ui/components/textarea/Textarea';
import { getInitials } from '@/utils/getInitials';

/**
 * LinkedIn rejects an invitation note over 299 characters (the API's `InvitationNote.MaxLength`). The count includes
 * the AI disclosure line Finlay adds to a first message.
 */
export const MESSAGE_LIMIT = 299;
/** Warn from here, before the limit blocks sending. */
const NEAR_LIMIT = MESSAGE_LIMIT - 20;

interface DraftEditorProps {
  candidateJob: CandidateJobApi;
  /** The text shown: the unsaved edit, else the draft Finlay wrote. */
  value: string;
  isLoading: boolean;
  onChange: (content: string) => void;
  /** 1-based position among the drafts, for "2 of 9". */
  position: number;
  total: number;
  onPrevious?: () => void;
  onNext?: () => void;
}

/** One draft: who it goes to, why Finlay picked them, and the editable message. */
export function DraftEditor({
  candidateJob,
  value,
  isLoading,
  onChange,
  position,
  total,
  onPrevious,
  onNext,
}: DraftEditorProps) {
  const name = candidateFullName(candidateJob);
  const { location, linkedInProfileId } = candidateJob.candidate;
  const subtitle = [candidateCurrentWork(candidateJob), location]
    .filter(Boolean)
    .join(' · ');
  const strengths = candidateJob.strengths ?? [];
  const isTooLong = value.length > MESSAGE_LIMIT;
  const isNearLimit = !isTooLong && value.length > NEAR_LIMIT;
  const charactersLeft = MESSAGE_LIMIT - value.length;

  return (
    <div className="flex flex-col gap-5 p-4 sm:p-6">
      <div className="flex items-start gap-3">
        <span className="bg-muted text-muted-foreground grid size-10 shrink-0 place-items-center rounded-full text-xs font-semibold">
          {getInitials(name)}
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="truncate font-semibold">{name}</span>
          {subtitle && (
            <span className="truncate text-[13px] text-neutral-500">
              {subtitle}
            </span>
          )}
        </div>
        {linkedInProfileId && (
          <a
            className="text-primary-700 inline-flex shrink-0 items-center gap-1 text-[13px] font-medium hover:underline"
            href={`https://www.linkedin.com/in/${linkedInProfileId}`}
            rel="noopener noreferrer"
            target="_blank"
          >
            LinkedIn
            <ExternalLink className="size-3.5" />
          </a>
        )}
      </div>

      {strengths.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className="text-xs font-medium text-neutral-500">
            Why Finlay picked them
          </span>
          <ul className="flex flex-wrap gap-1.5">
            {strengths.map((strength) => (
              <li
                className="bg-muted rounded-md px-2 py-1 text-xs"
                key={strength.summary}
                title={strength.explanation}
              >
                {strength.summary}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between text-xs">
          <label
            className="font-medium text-neutral-500"
            htmlFor={`draft-${candidateJob.id}`}
          >
            Message
          </label>
          <span className="text-neutral-500 tabular-nums">
            {position} of {total}
          </span>
        </div>
        {isLoading ? (
          <div className="flex flex-col gap-2 rounded-md border p-3">
            <Skeleton className="h-3.5 w-full" />
            <Skeleton className="h-3.5 w-11/12" />
            <Skeleton className="h-3.5 w-2/3" />
          </div>
        ) : (
          <Textarea
            aria-describedby={`draft-${candidateJob.id}-count`}
            aria-invalid={isTooLong}
            className="min-h-32 leading-6"
            id={`draft-${candidateJob.id}`}
            onChange={(event) => onChange(event.target.value)}
            value={value}
          />
        )}
        <p
          className={cn(
            'flex justify-end text-xs tabular-nums',
            isTooLong
              ? 'text-destructive'
              : isNearLimit
                ? 'text-amber-700'
                : 'text-neutral-500',
          )}
          id={`draft-${candidateJob.id}-count`}
        >
          {isTooLong && (
            <span className="mr-auto">
              LinkedIn allows {MESSAGE_LIMIT} characters.
            </span>
          )}
          {isNearLimit && (
            <span className="mr-auto">
              {charactersLeft}{' '}
              {charactersLeft === 1 ? 'character' : 'characters'} left
            </span>
          )}
          {value.length} / {MESSAGE_LIMIT}
        </p>
      </div>

      {total > 1 && (
        <div className="flex justify-end gap-2">
          <Button
            className="h-7 rounded-md"
            disabled={!onPrevious}
            onClick={onPrevious}
            size="sm"
            variant="outline"
          >
            <ArrowLeft className="size-3.5" />
            Previous
          </Button>
          <Button
            className="h-7 rounded-md"
            disabled={!onNext}
            onClick={onNext}
            size="sm"
            variant="outline"
          >
            Next
            <ArrowRight className="size-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}
